import {
    Component, Input, Output, EventEmitter, OnDestroy, OnInit, HostListener, ElementRef, ViewChild
} from '@angular/core';
import { DateRange, DateConversion, PresetDateRangeCategory } from './date-range';
import { SessionStateService } from '../../core/user/session-state.service';
import * as joda from '@js-joda/core';
import { Duration, LocalDate, LocalDateTime, Period } from "@js-joda/core";

const DATE_PARSER = joda.DateTimeFormatter.ofPattern('M/d/yyyy');
const DATE_FORMATTER = DATE_PARSER;

/** Type to designate which date we're dealing with */
type WhichDateType = 'start'|'end';

/**
 * U/I for selecting a start and end date.
 * Automatically retains in session storage.
 */
@Component({
    selector: 'my-date-range-selector',
    templateUrl: 'date-range-selector.component.html',
    //styleUrls: ['../../../../node_modules/ngx-bootstrap/datepicker/bs-datepicker.scss'],
    styles: [`
        input { min-width: 96px; max-width: 96px; }
        .small {height: 20px; }
        .small-icon {padding: 0; height: 20px;}
        .input-group {display: table;}
        .bi-calendar-week-fill::before { font-size: 1.2em; vertical-align: middle; }
        .input-group-addon { display: table-cell; text-align: center; padding: 0;
                            border-radius: 4px; border-bottom-left-radius: 0; border-top-left-radius: 0; }
    `
    ]
})
export class DateRangeComponent implements OnInit, OnDestroy {

    @Input() fromLabel: boolean;

    @Input() small: boolean;

    @Input() disabled?: boolean = false;

    @Input()
    category: PresetDateRangeCategory = 'calendar';

    @Input() allowBlanks?: boolean = false;

    @Input() skipDataStore?: boolean = false;

    @Input() defaultStartDate: string | Date;

    @Input() defaultEndDate: string | Date;

    @Input() minDate: Date;
    @Input() maxDate: Date;

    @Input() maxNumberOfDays: number;

    /** Event emitted to inform the caller that there was an error */
    @Output() errorState = new EventEmitter<string>();
    @Output() dateRangeValidated = new EventEmitter<boolean>();

    /** Last selection that was emitted by selectedChange */
    lastSelected: DateRange;

    /** Event back to the caller when the date range changes */
    @Output()
    selectedChange = new EventEmitter<DateRange>();

    @ViewChild('startDateRef', {static: true}) startDateRef: ElementRef;
    @ViewChild('endDateRef', {static: true}) endDateRef: ElementRef;

    // Must convert everything to JS Date for Bootstrap datepicker. :-(
    startDate: Date;
    endDate: Date;
        
    // Validity checking
    startDateValid = true;
    endDateValid = true;

    // Popup toggles
    showStartPicker: boolean = false;
    showEndPicker: boolean = false;

    /** Track whether or not the mouse is inside or outside of this component */
    outside: boolean = false;

    constructor(private selfRef: ElementRef,
                private sessionState: SessionStateService) {
    }

    /**
     * Upon initialization, restore previous selection.
     */
    ngOnInit() {
        this.refresh();

        setTimeout(()=>{
            this.selectedChange.emit(this.lastSelected);
        });

    }

    ngOnDestroy() {
        this.close();
    }

    /**
     * Refresh display from session state.
     */
    refresh() {
        // Emit upon initialization to set the current value in the parent.
        let selected: DateRange;
        if (((!this.defaultStartDate || !this.defaultEndDate) && this.allowBlanks)
            || (this.defaultStartDate && this.defaultEndDate && this.skipDataStore)) {
            selected = new DateRange();

            if(this.defaultStartDate) {
                if(typeof this.defaultStartDate === "string")
                {
                    let dateString: string = this.defaultStartDate as string;
                    selected.startDate = LocalDateTime.parse(dateString).toLocalDate();
                }else{
                    let dateObj: Date = this.defaultStartDate as Date;
                    selected.startDate = DateConversion.jsToLocalDate(dateObj);
                }
            }

            if(this.defaultEndDate) {
                if(typeof this.defaultEndDate === "string")
                {
                    let dateString: string = this.defaultEndDate as string;
                    selected.endDate = LocalDateTime.parse(dateString).toLocalDate();
                }else{
                    let dateObj: Date = this.defaultEndDate as Date;
                    selected.endDate = DateConversion.jsToLocalDate(dateObj);
                }
            }
        } else {
            selected = this.sessionState.getDateRange(this.category);
        }

        this.startDate = selected.startJsDateLocal;
        this.endDate = selected.endJsDateLocal;

        this.setInputValue(this.startDateRef, selected.startDate ? DATE_FORMATTER.format(selected.startDate) : "");

        this.setInputValue(this.endDateRef, selected.endDate ? DATE_FORMATTER.format(selected.endDate) : "");

        this.lastSelected = selected;
    }

    /** User is typing in a start date */
    onStartDateKeyup() {
        let str = this.getInputValue(this.startDateRef);
        let input = this.parseDateStr(str);

        if (input != null || (this.allowBlanks && !str)) {
            this.startDate = DateConversion.localDateToStartOfDayJsLocal(input);
            this.startDateValid = true;
        }
        else {
            this.startDateValid = false;
            console.log("startDate invalid");
        }
    }

    /** User is typing in an end date */
    onEndDateKeyup() {
        let str = this.getInputValue(this.endDateRef);
        let input = this.parseDateStr(str);

        if (input != null || (this.allowBlanks && !str)) {
            this.endDate = DateConversion.localDateToStartOfDayJsLocal(input);
            this.endDateValid = true;
        }
        else {
            this.endDateValid = false;
            console.log("endDate invalid");
        }
    }

    /**
     * User has finished typing into an input field.
     */
    onExitDateInput(priority: WhichDateType) {
        // Update string form displayed
        if (!this.startDate) {
            if(!this.allowBlanks || (this.allowBlanks && this.getInputValue(this.startDateRef))){
                this.setInputValue(this.startDateRef, "");
                this.startDateValid = this.allowBlanks;
            }
        }

        if (!this.endDate) {
            if(!this.allowBlanks || (this.allowBlanks && this.getInputValue(this.endDateRef))) {
                this.setInputValue(this.endDateRef, "");
                this.endDateValid = false;
            }
        }

        this.processDates(priority);
    }

    /**
     * User selected a new date via a date picker U/I.
     * @param which Was the start or end date picked?
     */
    datePicked(newValue: Date, which: WhichDateType) {
        // Delay to give ngModel a chance to modify startDate/endDate
        setTimeout(() => {
            if (newValue) {
                this.close();

                // Date picker always produces a valid date.
                if (which === 'start') {
                    this.startDate = newValue;
                    this.startDateValid = true;
                }
                else {
                    this.endDate = newValue;
                    this.endDateValid = true;
                }
    
                this.processDates(which);
            }
        });
    }
    
    /**
     * One or both dates have changed - evaluate.
     * @param priority if there is an ordering conflict between start and end, which one do we keep?
     */
    private processDates(priority: WhichDateType) {

        if (this.startDate) {
            this.startDateValid = this.validateDateAndUpdateUI(this.startDateRef, this.startDate);
        }
        else if (this.allowBlanks && this.endDate){
            // If user has cleared the state, clear everything
            if(!this.getInputValue(this.startDateRef) && priority === 'start') {
                this.endDate = this.startDate;
                this.setInputValue(this.endDateRef, null);
            }
            else{
                this.startDate = this.endDate;
                this.setInputValue(this.startDateRef, DATE_FORMATTER.format(DateConversion.jsToLocalDate(this.startDate)));
                this.startDateValid = true;
            }
        }

        if (this.endDate) {
            this.endDateValid = this.validateDateAndUpdateUI(this.endDateRef, this.endDate);
        }
        else if (this.allowBlanks && this.startDate){
            // If user has cleared the state, clear everything
            if(!this.getInputValue(this.endDateRef) && priority === 'end') {
                this.startDate = this.endDate;
                this.setInputValue(this.startDateRef, null);
            }else{
                this.endDate = this.startDate;
                this.setInputValue(this.endDateRef, DATE_FORMATTER.format(DateConversion.jsToLocalDate(this.endDate)));
                this.endDateValid = true;
            }
        }
        
        if (this.startDate && this.endDate) {
            if(this.maxNumberOfDays) {

                let localStartDate = DateConversion.jsToLocalDate(this.startDate).atStartOfDay();
                let localEndDate = DateConversion.jsToLocalDate(this.endDate).atStartOfDay();
                let newLocalEndDate = localStartDate.plusDays(this.maxNumberOfDays);
                let currentNumberOfDays = Duration.between(localStartDate, localEndDate).toDays();
                if(newLocalEndDate <= localEndDate){
                    this.startDateValid = false;
                    this.endDateValid = false;
                    let selected = new DateRange();
                    selected.startJsDateUTC = this.startDate;
                    selected.endJsDateUTC = this.endDate;
                    this.lastSelected=selected;
                    let numbersofBaysBetweenDate = currentNumberOfDays+1;// added this magic number to include the first day as the between Date  implementation does not include first day
                    this.errorState.emit('Date range must be less than '+ this.maxNumberOfDays + ' days. Currently ' + numbersofBaysBetweenDate + ' days');
                }

            }
        }

        // Nothing until both dates are set
        if (!this.startDate || !this.endDate) {
            // Emit that the range is invalid Derek Blank
            if(this.allowBlanks) {
                let selected = new DateRange();
                selected.startJsDateUTC = this.startDate;
                selected.endJsDateUTC = this.endDate;
                this.lastSelected=selected;
                this.selectedChange.emit(selected);
            }
        }
        else if (this.startDateValid && (this.endDate && this.endDateValid)) {
            if (this.startDate.getTime() > this.endDate.getTime()) {
                console.log("start date is after end date - adjusting");
                // Start can't be after end!
                if (priority === 'start') {
                    this.endDate = this.startDate;
                    this.setInputValue(this.endDateRef, this.getInputValue(this.startDateRef));
                }
                else {
                    this.startDate = this.endDate;
                    this.setInputValue(this.startDateRef, this.getInputValue(this.endDateRef));
                }
            }

            // Emit & save change
            let selected = new DateRange();
            selected.startJsDateUTC = this.startDate;
            selected.endJsDateUTC = this.endDate;

            if (!this.lastSelected.isEqual(selected)) {
                console.log("New date range: " + selected.toString());
                this.lastSelected = selected;
                this.selectedChange.emit(selected);

                if(!this.skipDataStore)
                    this.sessionState.setCustomDateRange(this.category, selected);
            }
        }else if (this.startDateValid) {
            // Emit & save change
            let selected = new DateRange();
            selected.startJsDateUTC = this.startDate;
            selected.endJsDateUTC = this.endDate;

            if (!selected.isEqual(this.lastSelected)) {
                console.log("New date range: " + selected.toString());
                this.lastSelected = selected;
                this.selectedChange.emit(selected);

                if(!this.skipDataStore)
                    this.sessionState.setCustomDateRange(this.category, selected);
            }
        }

        let isValid = this.startDateValid && this.endDateValid;
        this.dateRangeValidated.emit(isValid);
    }

    validateDateAndUpdateUI(input: ElementRef, date: Date): boolean {
        let localDate = DateConversion.jsToLocalDate(date);
        this.setInputValue(input, DATE_FORMATTER.format(localDate));

        if (this.minDate) {
            let minLocalDate = DateConversion.jsToLocalDate(this.minDate);

            if (minLocalDate.isAfter(localDate)) {
                return false;
            }
        }

        if (this.maxDate) {
            let maxLocalDate = DateConversion.jsToLocalDate(this.maxDate);

            if (localDate.isAfter(maxLocalDate)) {
                return false;
            }
        }

        return true;
    }

    /**
     * Close the popup date pickers.
     */
    close() {
        this.showStartPicker = false;
        this.showEndPicker = false;
    }

    /** Track whether or not the mouse is inside or outside of this component */
    @HostListener('mouseenter')
    onMouseEnter() {
        this.outside = false;
    }

    /** Track whether or not the mouse is inside or outside of this component */
    @HostListener('mouseleave')
    onMouseLeave() {
        this.outside = true;
    }

    /** If user clicks while outside this component, close the popups. */
    @HostListener('document:mouseup')
    onClickSomewhere() {
        if (this.outside)
            this.close();
    }

    //todo verify and abstract...
    handleBlur(e: Event) {
        (e.target as HTMLElement).blur();
    }

    /**
     * Pares a user input date into a Joda Date - or null if not valid.
     *
     * @param str user input
     * @returns {joda.LocalDate} or {null}
     */
    private parseDateStr(str: string): joda.LocalDate {
        try {
            return joda.LocalDate.from(DATE_PARSER.parse(str));
        }
        catch (failure) {
            return null;
        }
    }

    /** Helper to set the value in a referenced HTMLInputElement */
    private setInputValue(element: ElementRef, value: string) {
        (element.nativeElement as HTMLInputElement).value = value;
    }

    /** Helper to get the value in a referenced HTMLInputElement */
    private getInputValue(element: ElementRef): string {
        return (element.nativeElement as HTMLInputElement).value;
    }

    /** Processes the date range and emits whether the date range is valid */
    public validateDateRange() {
        this.processDates("start");
    }
}
